import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Autoplay from "embla-carousel-autoplay";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Typewriter } from "@/components/Typewriter";
import { SiteFooter } from "@/components/SiteFooter";
import { StudyGuides } from "@/components/StudyGuides";
import { FaqSection, faqJsonLd } from "@/components/FaqSection";
import { LearningJourney } from "@/components/LearningJourney";
import { useSiteSettings } from "@/lib/settings";
import { SERVICES } from "@/data/services";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import {
  ArrowRight,
  Users,
  Award,
  MessageCircle,
  GraduationCap,
  VolumeX,
  CheckCircle2,
  Star,
  Sparkles,
  Gift,
  Library,
} from "lucide-react";


// Order requested by client

const TESTIMONIALS = [
  { text: "My experience with Tutors Melbourne has been the best learning experience I've had with any teacher or tutor. The content was made so simple and taught so well.", author: "Ryan", role: "VCE Student" },
  { text: "Since commencing working with Tutors Melbourne, my son is a lot more confident with his work and his grades have improved. Definitely recommended!", author: "Helen", role: "Parent" },
  { text: "We've had a wonderful experience with Tutors Melbourne. They are incredibly knowledgeable, reliable, and genuinely supportive. My daughter has grown so much in confidence since starting.", author: "Cindi", role: "Parent" },
  { text: "The team has made classes really fun and engaging and explained things in such a way that everyone was able to clearly understand key concepts. Coming to class was enjoyable — positive vibes, smiles on every student's face.", author: "Sanjay", role: "VCE Student" },
  { text: "The team was there every week helping my daughter from the very start of her Year 12 journey up until the day before the exam. Could not recommend them more for anyone needing tutoring.", author: "Liat", role: "Parent" },
  { text: "Very professional tutoring with an immense amount of experience for students going into or doing VCE. They have been especially helpful with English & effective writing!", author: "Jack", role: "VCE Student" },
  { text: "I went from struggling to study and being unmotivated, to studying passionately and achieving highly. Without the strong insights I received I would have wasted my time with the incorrect techniques and never gained the confidence to achieve the results I had the capacity to achieve.", author: "Judd", role: "VCE Student" },
  { text: "Brilliant tutoring — they knew the content inside out and my son walked into every SAC confident and getting good marks. They were always available to read my son's work and mark it outside of tutoring hours. Tutors Melbourne went above and beyond.", author: "Natalie", role: "Parent" },
  { text: "Yahav helped me get my highest English mark in years. I was averaging 45–50% in English and he helped bring me up to 70% on average.", author: "Will", role: "Student" },
  { text: "Immense depth of knowledge of the ideas within the content, along with expertise in the study designs, contributed to an experience that exceeded my greatest expectations. Working with Tutors Melbourne didn't feel like work, but an experience of enticement and fulfilment.", author: "Leo", role: "VCE Student" },
  { text: "Tutors Melbourne has been an amazing help. Very approachable tutors who helped me gain confidence in VCE.", author: "Daniel", role: "VCE Student" },
  { text: "Even in the short time I was tutored, my marks have improved significantly. The openness allowed me to express my concerns without pressure or awkwardness. I definitely recommend to others who want to improve.", author: "Kate", role: "VCE Student" },
  { text: "Tutors Melbourne's teaching style is unique and highly effective because they teach the techniques that real students use in exams. Anyone would benefit from their approach and guidance.", author: "Josh", role: "Student" },
  { text: "Yahav is an amazing tutor. He's been my English tutor for a few months now and has improved my English skills dramatically. He has so many tips and tricks that he is passionate to share. I would highly recommend him.", author: "Amy", role: "VCE Student" },
  { text: "Tutors Melbourne helped me accomplish what my other teachers couldn't in 6 years of high school. Clear and concise feedback, drastically improving my confidence. Unique and insightful lessons and methods for excelling in the final exam.", author: "Priyansh", role: "VCE Student" },
  { text: "Amazing tutoring! Intelligent, concise and in depth in the tutes — would definitely recommend!", author: "Joseph", role: "VCE Student" },
];

const FEATURES = [
  { icon: Award, title: "Specialists in the VCE Study Designs", desc: "Formal classroom teaching experience, VCE and/or university teaching experience with an ability to explain concepts clearly." },
  { icon: MessageCircle, title: "24/7 tutoring support", desc: "Access to all tutors at all hours to clarify ideas, answer questions and provide feedback." },
  { icon: CheckCircle2, title: "Vetted & Qualified Tutors", desc: "Hand-picked for academic brilliance and the ability to mentor and inspire." },
  { icon: Library, title: "Comprehensive Resources", desc: "Practice tests, in-depth notes, lesson plans and top scoring exemplars all provided to support your learning." },
];

// Count-up hook — animates from 0 to target when section is in view
function useCountUp(target, { duration = 1600, decimals = 0, suffix = "", prefix = "" } = {}) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const startTs = performance.now();
          const tick = (now) => {
            const p = Math.min(1, (now - startTs) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(eased * target);
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.3 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target, duration]);

  const display = decimals > 0 ? val.toFixed(decimals) : Math.round(val).toLocaleString();
  return { ref, display: `${prefix}${display}${suffix}` };
}

function StatCounter({ target, suffix = "", prefix = "", decimals = 0, label, testId }) {
  const { ref, display } = useCountUp(target, { decimals, suffix, prefix });
  return (
    <div ref={ref} className="text-center" data-testid={testId}>
      <div className="font-heading text-4xl md:text-6xl font-bold text-brand-gold mb-2 tabular-nums">{display}</div>
      <p className="text-xs md:text-sm text-white/70 font-body uppercase tracking-wider">{label}</p>
    </div>
  );
}

export default function LandingPage() {
  const settings = useSiteSettings();
  const term = settings.term_label;
  const heroVideoRef = useRef(null);
  const [heroMuted, setHeroMuted] = useState(true);

  const toggleHeroSound = () => {
    const v = heroVideoRef.current;
    if (!v) return;
    v.muted = false;
    v.volume = 1;
    setHeroMuted(false);
    const p = v.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  };
  // Stable autoplay plugin instances — recreated only on mount
  const testimonialsAutoplay = useRef(Autoplay({ delay: 4500, stopOnInteraction: false, stopOnMouseEnter: true }));

  return (
    <div className="min-h-screen bg-white dark:bg-[#0f1117] transition-colors duration-300" data-testid="landing-page">
      <Seo
        title="Melbourne’s Leading Small Group English Classes"
        rawTitle
        description="Small online and in-person group English classes for Year 7–12 in Melbourne, taught by tutors who scored 50 raw. Only 12 small groups available."
        path="/"
        jsonLd={faqJsonLd}
      />
      {/* Top promo bar */}
      <Link to="/enrol" className="block bg-brand-gold text-white text-center py-2 px-4 text-sm font-body font-semibold hover:bg-brand-gold-hover transition-colors group" data-testid="top-trial-bar">
        <Gift className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
        Only 12 small groups available for {term} — enrol early
        <ArrowRight className="inline h-3.5 w-3.5 ml-1.5 -mt-0.5 group-hover:translate-x-1 transition-transform" />
      </Link>

      <Navbar />

      {/* PAGE 1 — Hero */}
      <section className="hero-gradient dark:bg-transparent pt-20 md:pt-28 pb-16 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in-up">
            <Badge className="bg-brand-purple-soft text-brand-purple mb-6 font-body font-semibold px-4 py-1.5 text-sm rounded-full" data-testid="hero-badge">
              Not just another English tutor
            </Badge>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white tracking-tight leading-tight mb-6 min-h-[3.5em] sm:min-h-[3em]">
              Pairing Melbourne students with{" "}
              <span className="text-brand-purple dark:text-brand-purple-light">
                <Typewriter phrases={["English tutors who scored 50 raw", "small groups that actually get results", "the confidence to walk into any exam"]} />
              </span>
            </h1>
            <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed mb-6 max-w-lg font-body">
              Small online and in-person group classes for Year 7–12 English.
            </p>

            {/* Scarcity callout */}
            <div className="mb-8 rounded-2xl border-2 border-brand-gold bg-brand-gold/10 p-5 max-w-lg" data-testid="scarcity-callout">
              <p className="font-heading text-2xl md:text-3xl font-bold text-brand-purple dark:text-brand-gold leading-tight">
                Only 12 small groups available
              </p>
              <p className="font-body text-sm text-slate-700 dark:text-slate-300 mt-1.5">
                Once filled, no more students for 2027.
              </p>
              <p className="font-body text-sm font-semibold text-brand-purple dark:text-brand-purple-light mt-2">
                Enroll early for {term}. Secure your spot before the 2027 rush.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Link to="/enrol" data-testid="hero-cta-trial">
                <Button className="bg-brand-gold hover:bg-brand-gold-hover text-white rounded-full px-8 py-6 font-bold text-base shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 font-body">
                  Enrol For {term} <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/pricing" data-testid="hero-cta-subjects">
                <Button variant="outline" className="rounded-full px-8 py-6 font-bold text-base border-brand-purple text-brand-purple hover:bg-brand-purple-soft dark:border-brand-purple-light dark:text-brand-purple-light dark:hover:bg-brand-purple/20 font-body">
                  See Pricing
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-4 text-slate-600 dark:text-slate-300 font-body">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="w-8 h-8 rounded-full bg-brand-purple-soft dark:bg-brand-purple/30 border-2 border-white dark:border-[#0f1117] flex items-center justify-center">
                    <Star className="h-3 w-3 text-brand-gold fill-brand-gold" />
                  </div>
                ))}
              </div>
              <span className="text-base md:text-lg font-semibold">Trusted by 70+ Melbourne families</span>
            </div>
          </div>

          <div className="animate-slide-in-right delay-200 order-first lg:order-none mb-4 lg:mb-0">
            <div className="relative mx-auto w-full max-w-[280px] sm:max-w-[320px] lg:max-w-[340px]">
              <div className="absolute -inset-4 bg-brand-purple/5 rounded-3xl -rotate-3" />
              <video
                ref={heroVideoRef}
                src="/video/hero.mp4"
                poster="/video/hero-poster.jpg"
                autoPlay
                muted={heroMuted}
                loop
                playsInline
                webkit-playsinline="true"
                x5-playsinline="true"
                disablePictureInPicture
                preload="metadata"
                controls
                controlsList="nodownload"
                onVolumeChange={(e) => setHeroMuted(e.currentTarget.muted)}
                className="relative rounded-2xl shadow-xl object-cover w-full aspect-[9/16] max-h-[440px] sm:max-h-[500px] lg:max-h-[560px] bg-slate-900"
                data-testid="hero-video"
              />
              {heroMuted && (
                <button
                  type="button"
                  onClick={toggleHeroSound}
                  className="absolute inset-x-0 bottom-16 mx-auto w-fit flex items-center gap-2 rounded-full bg-brand-purple/90 hover:bg-brand-purple text-white px-5 py-2.5 text-sm font-body font-semibold shadow-lg backdrop-blur-sm transition-all hover:-translate-y-0.5"
                  data-testid="hero-video-unmute"
                >
                  <VolumeX className="h-4 w-4" /> Tap for sound
                </button>
              )}
              <div className="absolute -top-3 left-1 sm:-top-4 sm:-left-3 lg:-top-5 lg:-left-6 bg-white dark:bg-[#1a1b23] rounded-xl shadow-lg p-3 lg:p-4 flex items-center gap-2.5 lg:gap-3 border border-slate-100 dark:border-slate-800 max-w-[calc(100%-0.5rem)]">
                <div className="bg-brand-gold rounded-full p-1.5 lg:p-2 shrink-0"><GraduationCap className="h-4 w-4 lg:h-5 lg:w-5 text-white" /></div>
                <div className="min-w-0">
                  <p className="font-body font-bold text-slate-900 dark:text-white text-xs lg:text-sm truncate">VCE Excellence</p>
                  <p className="text-[11px] lg:text-xs text-slate-500 dark:text-slate-400 font-body truncate">Personalised lesson plans</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PAGE 2 — Testimonials carousel */}
      <section className="py-16 px-6 bg-slate-50 dark:bg-[#0a0b10]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-3">What students & parents say</h2>
            <div className="flex items-center justify-center gap-1 mb-2">
              {[1, 2, 3, 4, 5].map((i) => <Star key={i} className="h-5 w-5 text-brand-gold fill-brand-gold" />)}
            </div>
            <p className="text-slate-600 dark:text-slate-400 font-body text-base md:text-lg font-semibold">Trusted by 70+ Melbourne families</p>
          </div>

          <Carousel opts={{ align: "start", loop: true }} plugins={[testimonialsAutoplay.current]} className="w-full">
            <CarouselContent className="-ml-4">
              {TESTIMONIALS.map((t, i) => (
                <CarouselItem key={i} className="pl-4 sm:basis-1/2 lg:basis-1/3" data-testid={`testimonial-${i}`}>
                  <div className="bg-white dark:bg-[#1a1b23] rounded-2xl p-6 border border-slate-100 dark:border-slate-800 h-full flex flex-col">
                    <div className="flex gap-1 mb-3">
                      {[1, 2, 3, 4, 5].map((s) => <Star key={s} className="h-4 w-4 text-brand-gold fill-brand-gold" />)}
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-300 font-body italic leading-relaxed mb-4 flex-1">"{t.text}"</p>
                    <div className="flex items-center gap-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                      <div className="w-9 h-9 rounded-full bg-brand-purple-soft dark:bg-brand-purple/30 flex items-center justify-center font-heading font-bold text-brand-purple dark:text-brand-purple-light text-sm">
                        {t.author[0]}
                      </div>
                      <div>
                        <p className="font-body font-semibold text-slate-900 dark:text-white text-sm">{t.author}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 font-body">{t.role}</p>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden sm:flex -left-2" data-testid="testimonials-prev" />
            <CarouselNext className="hidden sm:flex -right-2" data-testid="testimonials-next" />
          </Carousel>
        </div>
      </section>

      {/* How we teach every lesson */}
      <LearningJourney />

      {/* A peek at some of our work */}
      <StudyGuides />

      {/* Internal links to our class pages + free text guides */}
      <section className="py-16 px-6 bg-slate-50 dark:bg-[#141620]" data-testid="classes-index-section">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-3">
              Find the right class
            </h2>
            <p className="text-base text-slate-600 dark:text-slate-300 font-body mb-8 max-w-2xl">
              English-only, small groups, and taught by tutors who scored at the very top of the state.
              Have a read of whichever fits your student.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              ...SERVICES.map((s) => ({ to: `/${s.slug}`, title: s.title, blurb: s.nav })),
              { to: "/guides", title: "Free VCE text guides", blurb: "Text Analysis" },
            ].map((c) => (
              <Link key={c.to} to={c.to}
                className="group rounded-2xl bg-white dark:bg-[#1a1b23] border border-slate-200 dark:border-slate-800 p-6 hover:border-brand-purple/50 hover:shadow-md transition-all"
                data-testid={`home-service-link-${c.to.replace(/\//g, "")}`}>
                <p className="text-xs font-body font-semibold text-brand-gold mb-2">{c.blurb}</p>
                <h3 className="font-heading text-lg font-bold text-slate-900 dark:text-white leading-snug group-hover:text-brand-purple transition-colors">
                  {c.title}
                </h3>
                <span className="inline-flex items-center gap-1 text-sm font-body font-semibold text-brand-purple dark:text-brand-purple-light mt-3">
                  Read more <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PAGE 4 — 2025 Results */}
      <section className="py-20 px-6 bg-gradient-to-br from-brand-purple to-[#4a1e66] dark:from-[#3a1650] dark:to-[#1e0a2e] text-white" data-testid="results-section">
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-12">
            <Badge className="bg-brand-gold text-white mb-4 font-body font-semibold px-4 py-1.5 rounded-full">Let's Wrap Up 2025</Badge>
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">Results that speak for themselves</h2>
          </Reveal>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-14">
            <StatCounter target={97.3} decimals={1} label="Average student ATAR" testId="stat-0" />
            <StatCounter target={77} suffix="%" label="Students with 40+ study scores vs state avg 25%" testId="stat-1" />
            <StatCounter target={3000} suffix="+" label="Hours of tutoring" testId="stat-2" />
            <StatCounter target={70} suffix="+" label="Students taught" testId="stat-3" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10" data-testid="student-spotlight">
              <Badge className="bg-brand-gold/20 text-brand-gold border border-brand-gold/30 mb-4 font-body font-semibold px-3 py-1 rounded-full">Student Spotlight — 50 Raw VCE English</Badge>
              <div className="flex gap-1 mb-3">
                {[1, 2, 3, 4, 5].map((i) => <Star key={i} className="h-4 w-4 text-brand-gold fill-brand-gold" />)}
              </div>
              <p className="text-lg text-white/90 font-body italic leading-relaxed mb-4">
                "Amazing tutoring! Intelligent, concise and in depth in the tutes — would definitely recommend!"
              </p>
              <div className="flex items-center gap-3 pt-3 border-t border-white/10">
                <img src="/team/joseph.jpg" alt="Joseph Z, 99.90 ATAR student" loading="lazy" width="48" height="48" className="w-12 h-12 rounded-full object-cover ring-2 ring-brand-gold/40" />
                <div>
                  <p className="font-heading font-bold text-white">Joseph Z.</p>
                  <p className="text-xs text-brand-gold font-body">99.90 ATAR — Perfect Study Score 50</p>
                </div>
              </div>
            </div>

            <div className="bg-brand-gold rounded-2xl p-8 flex flex-col justify-center items-center text-center" data-testid="enroll-cta">
              <h3 className="font-heading text-3xl md:text-4xl font-bold text-brand-purple mb-3">Enrol now for {term}</h3>
              <p className="text-brand-purple/80 font-body mb-6">Limited spots available. We call every family personally to match you with the right tutor.</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Link to="/enrol" data-testid="results-cta-trial">
                  <Button className="bg-brand-purple hover:bg-brand-purple-hover text-white rounded-full px-6 py-5 font-bold font-body">
                    Enrol For {term} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/pricing" data-testid="results-cta-pricing">
                  <Button variant="outline" className="rounded-full px-6 py-5 font-bold font-body bg-transparent border-brand-purple text-brand-purple hover:bg-brand-purple hover:text-white">
                    See pricing
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PAGE 7 — About Us */}
      <section id="about" className="py-20 px-6 bg-white dark:bg-[#0f1117]" data-testid="about-section">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <Badge className="bg-brand-gold/10 text-brand-gold mb-4 font-body font-semibold px-3 py-1 rounded-full">About Us</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-6 leading-tight">
              We built the tutoring experience{" "}
              <span className="text-brand-purple dark:text-brand-purple-light">we always wished we had.</span>
            </h2>
            <div className="space-y-4 text-slate-600 dark:text-slate-300 font-body leading-relaxed">
              <p>When we finished our own VCE journey, we noticed a significant gap in the tutoring industry. Students were forced to choose between flawed options: <strong className="text-slate-900 dark:text-white">massive, impersonal classes, expensive one-on-one sessions, or small groups with limited resources.</strong> None of these truly delivered the holistic support needed to excel.</p>
              <p className="text-brand-purple dark:text-brand-purple-light font-semibold">We asked ourselves: what if you could get the best of all three?</p>
              <p>That's exactly what you'll find at Tutors Melbourne — a supportive environment that delivers exceptional value by combining structured, personalised lessons with small, collaborative groups and premium resources.</p>
              <p className="text-sm italic text-slate-500 dark:text-slate-400 pt-2 border-l-4 border-brand-gold/60 pl-4">We're not just your tutors — we're your friends and mentors; we have successfully walked the path you are on and are here to guide you every step of the way.</p>
            </div>
          </Reveal>
          <Reveal delay={140} className="bg-gradient-to-br from-brand-purple/5 to-brand-gold/5 dark:from-brand-purple/15 dark:to-brand-gold/10 rounded-3xl p-8 border border-brand-purple/10 dark:border-brand-purple/20" data-testid="founder-note">
            <Badge className="bg-brand-purple text-white mb-4 font-body font-semibold px-3 py-1 rounded-full">From Our Founder</Badge>
            <img src="/team/yahav-full.jpg" alt="Yahav Ben Yosef, founder of Tutors Melbourne and raw 50 VCE English tutor" loading="lazy" className="w-full rounded-2xl object-cover object-top max-h-[380px] mb-5" data-testid="founder-photo" />
            <div className="flex items-start gap-4 mb-4">
              <div>
                <h3 className="font-heading text-xl font-bold text-slate-900 dark:text-white">Yahav B Yosef</h3>
                <p className="text-sm font-body text-brand-purple dark:text-brand-purple-light font-semibold">Head of English & Founder</p>
                <p className="text-xs text-brand-gold font-body font-medium">50 RAW ENGLISH · 99 ATAR · 60+ students</p>
              </div>
            </div>
            <div className="space-y-3 text-sm text-slate-600 dark:text-slate-300 font-body leading-relaxed italic">
              <p>"Hey there 👋 Thank you for visiting our website! I wanted to share a little about myself and why I founded Tutors Melbourne."</p>
              <p>"After tutoring countless students from different schools, ages, levels and backgrounds, I have learnt how to effectively pass down the knowledge that allowed me to succeed in VCE and in Australia."</p>
              <p className="text-slate-900 dark:text-white font-semibold not-italic">I believe those who have knowledge should always pass it down to help others — that is why I opened Tutors Melbourne.</p>
              <p className="text-right font-heading text-brand-purple dark:text-brand-purple-light text-base not-italic pt-2">— Yahav</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PAGE 6 — Final CTA */}
      <section className="py-20 px-6 bg-slate-50 dark:bg-[#0a0b10]" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-brand-purple to-brand-purple/80 dark:from-brand-purple dark:to-[#4a1e66] rounded-3xl p-12 shadow-xl">
          <MessageCircle className="h-10 w-10 text-brand-gold mx-auto mb-4" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">Begin your journey</h2>
          <p className="text-white/80 font-body text-lg mb-8 max-w-2xl mx-auto">
            {settings.enrol_headline} Education is the most powerful weapon you can use to change the world — we're here to provide the tools.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/enrol" data-testid="footer-cta-trial">
              <Button className="bg-brand-gold hover:bg-brand-gold-hover text-white rounded-full px-8 py-6 font-bold font-body">
                Enrol For {term} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/pricing" data-testid="footer-cta-classes">
              <Button variant="outline" className="rounded-full px-8 py-6 font-bold font-body bg-transparent border-white text-white hover:bg-white hover:text-brand-purple">
                See Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <FaqSection />

      {/* PAGE 8 — Footer */}
      <SiteFooter />
    </div>
  );
}
